import { AlignmentType } from 'docx'
import { resolveGoPricingLayout } from '@/components/expGoPricing/goPricingSheetLayout'
import { createGoPricingLayoutBlocks } from '@/components/reportPresentation/allTables/createGoPricingSheetTable'
import { appendProcurementNotesToBlocks } from '@/components/reportPresentation/procurementDocxNotes'
import {
  createContentParagraph,
  createEmptyLine,
  createParagraph,
  createSeparateParagraphs,
} from './docxFunctions'
import { BOLD_TEXT, createCombinationParagraph } from './docxFunctions'

function createCostFieldBlocks(label, text) {
  const value = String(text || '').trim() || '—'

  return [
    createCombinationParagraph([{ type: BOLD_TEXT, content: `${label}` }]),
    ...createSeparateParagraphs(value, { alignment: AlignmentType.JUSTIFIED }),
    createEmptyLine(),
  ]
}

function formatStockLines(goPricing) {
  const forms = Array.isArray(goPricing?.releaseForms)
    ? goPricing.releaseForms
    : []
  return forms
    .filter(
      f =>
        String(f?.name || '').trim() &&
        Number.isFinite(Number(f?.packageCount))
    )
    .map(f => `${String(f.name).trim()} – ${Number(f.packageCount)} уп.`)
}

function stockAsOfDate(goPricing) {
  if (goPricing?.analysisDate) {
    const [y, m, d] = String(goPricing.analysisDate).split('-')
    if (y && m && d) return `${d}.${m}.${y}`
  }
  return new Date().toLocaleDateString('ru-RU')
}

export function createCostAndGoPricingDocxBlocks(cost, goPricing) {
  const blocks = [
    ...createCostFieldBlocks(
      'Потребность на одного пациента в год:',
      cost?.patientNeed
    ),
    ...createCostFieldBlocks(
      'Целевая популяция пациентов:',
      cost?.targetPatientPopulation
    ),
  ]

  if (!goPricing) return blocks

  const goRows = goPricing.goRows || []
  if (goRows.length) {
    const layout = resolveGoPricingLayout(goRows, 'default')
    blocks.push(
      ...createGoPricingLayoutBlocks(layout, {
        sectionTitle:
          layout.title || 'Выпуск в гражданский оборот (ГО) за календарный год',
      })
    )
  }

  const sheets = Array.isArray(goPricing.sheets) ? goPricing.sheets : []
  let planSectionTitleShown = false
  for (const sheet of sheets) {
    if (!(sheet?.rows || []).length) continue
    const layout = resolveGoPricingLayout(sheet.rows, 'default')
    const sectionTitle =
      layout.title || 'План поставок и выпуск в ГО на 2025–2026 гг.'
    const showSectionTitle = !planSectionTitleShown
    blocks.push(
      ...createGoPricingLayoutBlocks(layout, { sectionTitle, showSectionTitle })
    )
    if (layout?.emphasizeFirstRow) {
      planSectionTitleShown = true
    }
  }

  const stockLines = formatStockLines(goPricing)
  if (stockLines.length) {
    blocks.push(
      createContentParagraph(
        `Остатки лекарственного препарата по данным Федеральной государственной информационной системы мониторинга движения лекарственных препаратов для медицинского применения на территории Российской Федерации (по состоянию на ${stockAsOfDate(goPricing)}):`,
        { alignment: AlignmentType.JUSTIFIED },
        { bold: true, size: 24 },
        false
      )
    )
    stockLines.forEach(line => {
      blocks.push(createParagraph(`– ${line}`, { alignment: AlignmentType.LEFT }))
    })
    blocks.push(createEmptyLine())
  }

  const procurementRows = goPricing.procurementRows || []
  if (procurementRows.length) {
    const layout = resolveGoPricingLayout(procurementRows, 'procurement')
    blocks.push(
      ...appendProcurementNotesToBlocks(
        createGoPricingLayoutBlocks(layout, {
          sectionTitle:
            'Закупки предлагаемого ЛП (в соответствии с данными информационного ресурса «ИАС Закупки»)',
          trailingEmptyLine: false,
        }),
        layout,
        goPricing
      )
    )
  }

  const pricingRows = goPricing.pricingRows || []
  if (pricingRows.length) {
    const layout = resolveGoPricingLayout(pricingRows, 'pricing')
    blocks.push(
      ...createGoPricingLayoutBlocks(layout, {
        sectionTitle: 'Ценовая характеристика для предлагаемого ЛП',
      })
    )
  }

  return blocks
}
